import React, { useEffect, useRef } from 'react';
import './styles/AnimationPicture.css';
import yellowImg from '../assets/yellow.jpg';

const AnimationPicture = ({ onClose }) => {
  const containerRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!window.p5 || !containerRef.current) return;

    const container = containerRef.current;
    let p5Instance = null;

    const sketch = (p) => {
      let bg;
      let fArray = [];
      let hatchSize = 100;
      let hatchToggle = true;
      let chatchSize = 100;
      let hatchDelay = 1;
      let canvasW = 100;
      let canvasH = 100;

      p.preload = () => {
        bg = p.loadImage(yellowImg);
      };

      p.setup = () => {
        if (!container) return;

        const containerW = container.clientWidth || 360;
        const containerH = container.clientHeight || 450;

        const imgRatio = bg.width / bg.height;
        canvasW = containerW;
        canvasH = containerW / imgRatio;

        if (canvasH > containerH) {
          canvasH = containerH;
          canvasW = containerH * imgRatio;
        }

        p.createCanvas(canvasW, canvasH);
        p.strokeWeight(0.8);
        p.background(253, 253, 253);

        const numFocalPoints = 400; 
        const spacingX = canvasW / (numFocalPoints + 1);
        const spacingY = canvasH / (numFocalPoints + 1);

        for (let i = 1; i <= numFocalPoints; i++) {
          const focalPoint = p.createVector(i * spacingX, i * spacingY);
          fArray.push(focalPoint);
        }
      };

      p.draw = () => {
        if (!p._renderer || !bg) return;

        if (hatchToggle) {
          if (p.frameCount % hatchDelay === 0) {
            const drawsPerFrame = 6;
            for (let i = 0; i < drawsPerFrame; i++) {
              for (let j = 0; j < fArray.length; j++) {
                circleHatch(fArray[j].x, fArray[j].y);
              }
            }

            // Decrease hatch size
            if (hatchSize > 5) {
              hatchSize -= 0.5;
            }
          }
        }
      };

      const circleHatch = (cx, cy) => {
        if (!p._renderer || !bg) return;

        let x = p.random(0, canvasW);
        let y = p.random(0, canvasH);
        
        // Sampling pixel color exactly like the codepen logic
        let pixCol = bg.get(bg.width / (canvasW / x), bg.height / (canvasH / y));
        p.stroke(pixCol);
        
        let r = p.dist(cx, cy, x, y);
        let theta = p.atan((y - cy) / (x - cx));
        let hs = p.min(200, chatchSize / 10);
        let d = p.random(p.PI / (hs + 10), p.PI / hs);
        p.noFill();

        if (cx >= x && cy >= y) {
          theta += p.PI;
          p.arc(cx, cy, r * 2, r * 2, theta - d, theta + d);
        }
        if (cx >= x && cy < y) {
          theta -= p.PI;
          p.arc(cx, cy, r * 2, r * 2, theta - d, theta + d);
        }
        if (cx < x && cy <= y) {
          p.arc(cx, cy, r * 2, r * 2, theta - d, theta + d);
        }
        if (cx < x && cy > y) {
          p.arc(cx, cy, r * 2, r * 2, theta - d, theta + d);
        }
        chatchSize += 0.05;
      };

      p.mousePressed = () => {
        // Guard to prevent interactions if renderer has been cleaned up
        if (!p._renderer) return;

        if (p.mouseX >= 0 && p.mouseX <= canvasW && p.mouseY >= 0 && p.mouseY <= canvasH) {
          fArray = [];
          const numFocalPoints = 20;
          const spacingX = canvasW / (numFocalPoints + 1);
          const spacingY = canvasH / (numFocalPoints + 1);

          for (let i = 1; i <= numFocalPoints; i++) {
            const focalPoint = p.createVector(i * spacingX, i * spacingY);
            fArray.push(focalPoint);
          }
          chatchSize = 1;
          p.background(253, 253, 253);
        }
      };

      p.keyPressed = () => {
        if (!p._renderer) return;
        hatchToggle = !hatchToggle;
      };
    };

    // Instantiate p5 inside the container node
    p5Instance = new window.p5(sketch, container);

    return () => {
      if (p5Instance) {
        p5Instance.remove();
      }
      container.innerHTML = '';
    };
  }, []);

  // Handle clicking outside the modal content to close it
  const handleOverlayClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-card" ref={modalRef}>
        <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
          &times;
        </button>
        <h3 className="modal-title">A Masungit 💛</h3>
        <p className="modal-subtitle">Watching it paint in real-time... (Tap canvas to restart)</p>
        <div className="canvas-wrapper" ref={containerRef} />
      </div>
    </div>
  );
};

export default AnimationPicture;
